import { getAIQueue, closeAIQueue } from './ai-queue.js'
import { OpenAIService } from '../../services/openai.service.js'
import { AIRepository } from '../../modules/ai/ai.repository.js'
import { db } from '../database/firebase.js'
import { createLogger } from '../../common/utils/logger.js'
import { processPoiSuggestion } from '../../modules/ai/poi.processor.js'

const logger = createLogger('ai-worker')

/**
 * AI Worker
 * 
 * Processes AI jobs from the queue
 * Separated from AIService to avoid circular dependencies
 */

const openai = new OpenAIService()
const repository = new AIRepository()
const aiQueue = getAIQueue()

// ✅ OPTIMIZATION: Reduce concurrency in development to limit Redis requests
const poiConcurrency = process.env.NODE_ENV === 'production' ? 5 : 1
const planConcurrency = process.env.NODE_ENV === 'production' ? 2 : 1

logger.info('AI Worker starting', { poiConcurrency, planConcurrency })

// POI Suggestion worker
aiQueue.process('suggest-pois', poiConcurrency, async (job) => {
  return processPoiSuggestion(job.data)
})

// Plan Enhancement worker
aiQueue.process('enhance-plan', planConcurrency, async (job) => {
  const { planId, stops, userId } = job.data
  const startTime = Date.now()

  try {
    // Fetch plan from Firestore
    const planDoc = await db.collection('plans').doc(planId).get()
    
    if (!planDoc.exists) {
      throw new Error(`Plan ${planId} not found`)
    }

    const plan = planDoc.data()
    const planNameFromDoc = typeof plan?.name === 'string' ? plan.name.trim() : ''
    const fallbackPlanName = typeof job.data.planName === 'string' ? job.data.planName.trim() : ''
    const planName = planNameFromDoc || fallbackPlanName
    
    // Enhance description with OpenAI
    const enhancedDescription = await openai.enhancePlanDescription({
      name: planName,
      stages: plan?.stages || stops || []
    }, { userId, feature: 'ai:enhance-plan' })

    // Update plan in Firestore
    await db.collection('plans').doc(planId).update({
      description: enhancedDescription,
      enhancedAt: Date.now(),
      enhancedBy: 'ai'
    })

    const processingTimeMs = Date.now() - startTime

    // Save to analytics
    await repository.saveSuggestion({
      type: 'plan-enhancement',
      input: { planId, stops: stops || plan?.stages },
      output: { description: enhancedDescription },
      model: 'openai-' + (process.env.OPENAI_MODEL || 'gpt-4o-mini'),
      userId,
      timestamp: new Date(),
      metadata: {
        source: 'openai',
        processingTimeMs,
        generatedAt: new Date().toISOString(),
        cached: false
      }
    })

    logger.info(`✅ Plan ${planId} enhanced successfully in ${processingTimeMs}ms`)

    return {
      success: true,
      enhanced: true,
      description: enhancedDescription,
      processingTime: processingTimeMs,
      metadata: {
        source: 'openai',
        processingTimeMs,
        generatedAt: new Date().toISOString()
      }
    }
  } catch (error) {
    logger.error('Failed to enhance plan', { planId, error })
    throw error
  }
})

logger.info('AI worker processors initialized')

// Graceful shutdown to avoid dangling Redis connections
for (const signal of ['SIGINT', 'SIGTERM']) {
  process.once(signal, async () => {
    await closeAIQueue()
  })
}
